import {
  type ChatStepSlug,
  type DomainErrorCode,
  DOMAIN_ERROR_CODES,
  isChatStepSlug,
} from '@sigora/domain';

/**
 * A refusal the server meant, as opposed to a network failure. The code is what the UI branches
 * on — never the message — because the messages are prose the server is free to reword, and
 * because two refusals with the same status say very different things to a user: a guide that
 * moved under an editor is not a guide whose structure the user may not change.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly detail: Record<string, unknown>;
  /**
   * The whole body the refusal came in, for the few refusals that name something beside the
   * domain's three fields — the turn guard's, which names the step whose turn is running.
   */
  readonly body: Record<string, unknown>;

  constructor(
    code: string,
    message: string,
    status: number,
    detail: Record<string, unknown>,
    body: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.detail = detail;
    this.body = body;
  }
}

const isDomainErrorCode = (value: string): value is DomainErrorCode =>
  (DOMAIN_ERROR_CODES as readonly string[]).includes(value);

/**
 * Reads a failed response into an error. The body is the domain's `{error, message, detail}`
 * shape, but a proxy or a crash can produce something else entirely, so an unreadable body
 * becomes a generic error rather than an exception thrown while handling an exception.
 */
export const readApiError = async (response: Response): Promise<ApiError> => {
  const body = (await response.json().catch(() => null)) as {
    error?: unknown;
    message?: unknown;
    detail?: unknown;
  } | null;

  const code = typeof body?.error === 'string' ? body.error : 'request_failed';
  const message =
    typeof body?.message === 'string' ? body.message : 'Something went wrong. Please try again.';
  const detail =
    typeof body?.detail === 'object' && body.detail !== null
      ? (body.detail as Record<string, unknown>)
      : {};

  return new ApiError(
    code,
    message,
    response.status,
    detail,
    typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {},
  );
};

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

/**
 * The step whose turn a refused send is waiting behind, or null.
 *
 * The study runs one turn at a time across all its threads, so a send can be refused because of a
 * turn on another step entirely; the refusal names that step, and null covers a refusal of another
 * kind and a running turn whose step the server could not name.
 */
export const runningStepOf = (error: unknown): ChatStepSlug | null =>
  isApiError(error) && error.code === 'turn_already_running' && isChatStepSlug(error.body.step)
    ? error.body.step
    : null;

/**
 * Whether a failure is one of the domain's, which the UI has words for, rather than a transport
 * problem or a bug, which it does not.
 */
export const domainCodeOf = (error: unknown): DomainErrorCode | null =>
  isApiError(error) && isDomainErrorCode(error.code) ? error.code : null;

/**
 * Refusals that mean a follow-up moved under this tab — answered, chosen or asked about elsewhere —
 * so what the thread shows is out of date and is read again rather than explained.
 */
const FOLLOW_UP_MOVED_CODES: ReadonlySet<string> = new Set<DomainErrorCode>([
  'follow_up_already_finalized',
  'follow_up_updating',
  'follow_up_not_answered',
  'follow_up_not_retryable',
  'follow_up_answer_running',
]);

/** Whether a follow-up refusal says the thread is out of date (`FOLLOW_UP_MOVED_CODES`). */
export const followUpMoved = (error: unknown): boolean =>
  isApiError(error) && FOLLOW_UP_MOVED_CODES.has(error.code);
